import { Equipment, EquipmentType } from '../types/project';

export type ConcurrencyState = 'ON' | 'OFF' | 'SHOULDER';

type SeasonKey = 'Winter' | 'Spring' | 'Summer' | 'Fall';

const LOOKUP: Record<EquipmentType, Record<SeasonKey, ConcurrencyState>> = {
  'Heat Pump':    { Winter: 'ON',       Spring: 'SHOULDER', Summer: 'ON',       Fall: 'SHOULDER' },
  'Strip Heat':   { Winter: 'ON',       Spring: 'OFF',      Summer: 'OFF',      Fall: 'SHOULDER' },
  'A/C':          { Winter: 'OFF',      Spring: 'SHOULDER', Summer: 'ON',       Fall: 'SHOULDER' },
  'EVSE':         { Winter: 'ON',       Spring: 'ON',       Summer: 'ON',       Fall: 'ON'       },
  'Range':        { Winter: 'ON',       Spring: 'ON',       Summer: 'ON',       Fall: 'ON'       },
  'Dryer':        { Winter: 'ON',       Spring: 'ON',       Summer: 'ON',       Fall: 'ON'       },
  'Water Heater': { Winter: 'ON',       Spring: 'ON',       Summer: 'ON',       Fall: 'ON'       },
  'Pool Pump':    { Winter: 'OFF',      Spring: 'SHOULDER', Summer: 'ON',       Fall: 'SHOULDER' },
  'Pool Heater':  { Winter: 'OFF',      Spring: 'ON',       Summer: 'OFF',      Fall: 'ON'       },
  // Unclassified equipment is COUNTED by default. TrueLoad cannot know how an
  // unusual appliance behaves seasonally, and the safe assumption on a document
  // asserting service adequacy is that it runs during the peak. Leaving it out
  // by default made the safest-sounding choice quietly shrink the load. Being
  // ON rather than SHOULDER also routes it through the override path, so
  // excluding it costs the preparer a written reason.
  'Other':        { Winter: 'ON',       Spring: 'ON',       Summer: 'ON',       Fall: 'ON'       },
};

const REASONS: Record<EquipmentType, Record<SeasonKey, string>> = {
  'Heat Pump': {
    Winter: 'Heat pumps operate in heating mode during winter peaks; included as a concurrent load.',
    Spring: 'Spring peaks fall outside peak heating and cooling seasons; verify whether this unit will be operating during the peak period.',
    Summer: 'Heat pumps operate in cooling mode during summer peaks; included as a concurrent load.',
    Fall: 'Fall peaks fall outside peak heating and cooling seasons; verify whether this unit will be operating during the peak period.',
  },
  'Strip Heat': {
    Winter: 'Auxiliary strip heat runs during winter heating peaks; included as a concurrent load.',
    Spring: 'Strip heat is a heating load and is not expected to operate during a spring peak.',
    Summer: 'Strip heat is a heating load and is not expected to operate during a summer peak.',
    Fall: 'Fall peaks may or may not coincide with the heating season; verify whether strip heat will be operating during the peak period.',
  },
  'A/C': {
    Winter: 'Air conditioning is a cooling load and is not expected to operate during a winter peak.',
    Spring: 'Spring peaks may or may not coincide with cooling season; verify whether A/C will be operating during the peak period.',
    Summer: 'Air conditioning runs during summer cooling peaks; included as a concurrent load.',
    Fall: 'Fall peaks may or may not coincide with cooling season; verify whether A/C will be operating during the peak period.',
  },
  'EVSE': {
    Winter: 'EV charging can occur at any time regardless of season; included as a concurrent load.',
    Spring: 'EV charging can occur at any time regardless of season; included as a concurrent load.',
    Summer: 'EV charging can occur at any time regardless of season; included as a concurrent load.',
    Fall: 'EV charging can occur at any time regardless of season; included as a concurrent load.',
  },
  'Range': {
    Winter: 'Electric ranges operate year-round and are not limited to any season; included as a concurrent load.',
    Spring: 'Electric ranges operate year-round and are not limited to any season; included as a concurrent load.',
    Summer: 'Electric ranges operate year-round and are not limited to any season; included as a concurrent load.',
    Fall: 'Electric ranges operate year-round and are not limited to any season; included as a concurrent load.',
  },
  'Dryer': {
    Winter: 'Electric dryers operate year-round and are not limited to any season; included as a concurrent load.',
    Spring: 'Electric dryers operate year-round and are not limited to any season; included as a concurrent load.',
    Summer: 'Electric dryers operate year-round and are not limited to any season; included as a concurrent load.',
    Fall: 'Electric dryers operate year-round and are not limited to any season; included as a concurrent load.',
  },
  'Water Heater': {
    Winter: 'Electric water heaters operate year-round and are not limited to any season; included as a concurrent load.',
    Spring: 'Electric water heaters operate year-round and are not limited to any season; included as a concurrent load.',
    Summer: 'Electric water heaters operate year-round and are not limited to any season; included as a concurrent load.',
    Fall: 'Electric water heaters operate year-round and are not limited to any season; included as a concurrent load.',
  },
  'Pool Pump': {
    Winter: 'Pool pumps are typically not operated in winter when pools are closed or unused.',
    Spring: 'Pool operation during spring peaks is variable; verify whether the pool will be in active use during the peak period.',
    Summer: 'Pool pumps run throughout the summer season; included as a concurrent load.',
    Fall: 'Pool operation during fall peaks is variable; verify whether the pool will be in active use during the peak period.',
  },
  'Pool Heater': {
    Winter: 'Pool heaters are not typically operated in winter when pools are closed; excluded from concurrent load.',
    Spring: 'Pool heaters are used to warm pools at the start of the season; included as a concurrent load.',
    Summer: 'Pool water is typically warm in summer and pool heaters are not expected to be in regular use.',
    Fall: 'Pool heaters are used to extend the swimming season into fall; included as a concurrent load.',
  },
  'Other': {
    Winter: 'Equipment type is unclassified; included as a concurrent load unless the preparer documents otherwise.',
    Spring: 'Equipment type is unclassified; included as a concurrent load unless the preparer documents otherwise.',
    Summer: 'Equipment type is unclassified; included as a concurrent load unless the preparer documents otherwise.',
    Fall: 'Equipment type is unclassified; included as a concurrent load unless the preparer documents otherwise.',
  },
};

/**
 * Equipment rows start life with `equipment_type: ''` (no selection yet), so every
 * lookup here has to tolerate the empty case. `''` is never a key of LOOKUP/REASONS,
 * so it falls through to the same defaults an unrecognized type would.
 */
export function getConcurrencyState(type: EquipmentType | '', season: string): ConcurrencyState {
  const row = type === '' ? undefined : LOOKUP[type];
  if (!row) return 'SHOULDER';
  return row[season as SeasonKey] ?? 'SHOULDER';
}

export function generateConcurrencyReason(type: EquipmentType | '', season: string): string {
  const row = type === '' ? undefined : REASONS[type];
  if (!row) return 'Review seasonal operating conditions and confirm concurrent status manually.';
  return row[season as SeasonKey] ?? 'Review seasonal operating conditions and confirm concurrent status manually.';
}

export function getAutoFillValues(
  type: EquipmentType | '',
  season: string
): { isConcurrent: boolean; reason: string } {
  const state = getConcurrencyState(type, season);
  const reason = generateConcurrencyReason(type, season);
  return { isConcurrent: state === 'ON', reason };
}

export function isAutoFillable(
  eq: Pick<Equipment, 'concurrency_reason' | 'equipment_type'>,
  season: string | null
): boolean {
  if (eq.concurrency_reason === null) return true;
  if (!season || season === 'Unknown') return false;
  return eq.concurrency_reason === generateConcurrencyReason(eq.equipment_type, season);
}

/**
 * The date unclassified ("Other") equipment changed from excluded-by-default to
 * counted-by-default.
 *
 * A project created before this and carrying a counted "Other" load will show a
 * different adequacy result than its owner last saw — correctly, since the load
 * was previously under-counted, but without explanation it reads as the software
 * changing its mind. `showsUnclassifiedLoadNotice` drives that explanation.
 *
 * Safe to delete this and the notice once no active project predates it.
 */
export const UNCLASSIFIED_COUNTED_SINCE = '2026-08-06';

/**
 * True when a project predates the default change and holds a counted "Other"
 * load — i.e. exactly the projects whose number moved without the user touching
 * anything. New projects never see the notice; neither do old ones without an
 * unclassified load, because nothing about their result changed.
 */
export function showsUnclassifiedLoadNotice(
  createdAt: string | undefined,
  equipment: Pick<Equipment, 'equipment_type' | 'is_concurrent'>[]
): boolean {
  if (!createdAt || createdAt >= UNCLASSIFIED_COUNTED_SINCE) return false;
  return equipment.some(eq => eq.equipment_type === 'Other' && eq.is_concurrent);
}

/**
 * True when the preparer's concurrent/not-concurrent call disagrees with the one
 * TrueLoad would make for this type and season — i.e. the row has been overridden.
 */
export function isConcurrencyOverridden(
  eq: Pick<Equipment, 'equipment_type' | 'is_concurrent'>,
  season: string
): boolean {
  return eq.is_concurrent !== getAutoFillValues(eq.equipment_type, season).isConcurrent;
}

/**
 * True when the row carries an explanation the preparer actually wrote, rather
 * than TrueLoad's generated sentence (or nothing at all).
 *
 * This is what stops a report printing "Counted at peak: No" directly above
 * TrueLoad's own words saying the load *is* included — a contradiction in the
 * software's voice on a document going to an inspector.
 */
export function hasOwnConcurrencyReason(
  eq: Pick<Equipment, 'equipment_type' | 'concurrency_reason'>,
  season: string
): boolean {
  const reason = eq.concurrency_reason?.trim() ?? '';
  if (!reason) return false;
  return reason !== generateConcurrencyReason(eq.equipment_type, season);
}
